import { configure, defineRule } from 'vee-validate';
import { required, email, min, max, confirmed, digits, one_of as veeOneOf } from '@vee-validate/rules';
import type { Composer } from 'vue-i18n';
import { validatePostalCode } from '~~/helpers/validatePostalCode';
import { defaultCountryCode } from '~~/i18n';
import {
  FORBIDDEN_CHARACTERS_MESSAGE,
  PASSWORD_RULE_KEY,
  FALLBACK_MESSAGES,
} from '~~/constants/validation';
import { extractForbiddenCharacters } from '~~/server/commercetools/helpers/assertNoForbiddenCharacters';
import { STREET_NUMBER_VALIDATION_DISABLED } from '~~/constants/checkout';

type ValidationMessageContext = {
  label?: string;
  name?: string;
  field?: string;
  value?: unknown;
  rule?: {
    name?: string;
    params?: unknown[] | Record<string, unknown>;
  };
};


const hasForbiddenCharacters = (value?: string | null): boolean => {

  return extractForbiddenCharacters(value).length > 0;

};

const sanitizeValue = (value: unknown): string => {

  if (value === null || value === undefined) {

    return '';
  
  }

  return String(value).trim();

};

const stripAddressSuffix = (value?: string | null) => {

  if (!value) {

    return value ?? undefined;
  
  }

  return value.replace(/(Shipping|Billing)$/i, '');

};

const capitalizeFirst = (value?: string) => {

  if (!value) {

    return undefined;
  
  }

  return value.charAt(0).toUpperCase() + value.slice(1);

};

const resolveFieldLabel = (ctx: ValidationMessageContext, composer: Composer | undefined): string => {

  if (typeof ctx.label === 'string' && ctx.label.length > 0) {

    return ctx.label;
  
  }

  const baseName = stripAddressSuffix(ctx.name || ctx.field);
  const candidates = [baseName, capitalizeFirst(baseName)];

  for (const candidate of candidates) {

    if (!candidate) {

      continue;
    
    }

    const translationKey = `validation.fieldNames.${candidate}`;
    const translated = composer?.t?.(translationKey);

    if (translated && translated !== translationKey) {

      return translated as string;
    
    }
  
  }

  return ctx.name || ctx.field || 'field';

};

const translateRuleMessage = ({
  composer,
  ruleName,
  params,
}: {
  composer: Composer | undefined;
  ruleName: string;
  params: Record<string, unknown>;
}): string | null => {

  if (!composer?.t) {

    return null;
  
  }

  const key = `validation.${ruleName}`;
  const translated = composer.t(key, params);

  if (translated && translated !== key) {

    return translated as string;
  
  }

  return null;

};

const getActiveCountryCode = (composer: Composer | undefined) => {

  const country = composer?.localeProperties?.value?.country;

  return typeof country === 'string' && country.length > 0 ? country : defaultCountryCode;

};

export default defineNuxtPlugin((nuxtApp) => {

  const composer = nuxtApp.$i18n as Composer | undefined;

  defineRule('email', email);
  defineRule('required', required);
  defineRule('min', min);
  defineRule('max', max);
  defineRule('confirmed', confirmed);
  defineRule('digits', digits);
  defineRule('oneOf', veeOneOf);
  defineRule('rating', required);

  defineRule('password', (value: unknown) => {

    const input = sanitizeValue(value);

    if (!input) {

      return true;
    
    }

    const hasLetter = /[A-Za-z]/.test(input);
    const hasNumber = /\d/.test(input);

    if (hasLetter && hasNumber) {

      return true;
    
    }

    return composer?.t?.(PASSWORD_RULE_KEY) ?? PASSWORD_RULE_KEY;
  
  });

  defineRule('postalCode', (value: unknown) => {

    const input = sanitizeValue(value);

    if (!input) {

      return true;
    
    }

    return validatePostalCode(getActiveCountryCode(composer), input);
  
  });

  defineRule('streetNumber', (value: unknown) => {

    const country = getActiveCountryCode(composer);
    const streetValidationConfig = STREET_NUMBER_VALIDATION_DISABLED as Record<string, boolean>;

    if (streetValidationConfig?.[country]) {

      return true;
    
    }

    const input = sanitizeValue(value);

    if (!input) {

      return false;
    
    }

    return /\d+/.test(input);
  
  });

  defineRule('phoneNumber', (value: unknown) => {

    const input = sanitizeValue(value);

    if (!input) {

      return true;
    
    }

    const digitsOnly = input.replace(/\D/g, '');

    return digitsOnly.length >= 6 && digitsOnly.length <= 15;
  
  });

  defineRule('noForbiddenCharacters', (value: unknown) => {

    const input = sanitizeValue(value);

    if (!input) {

      return true;
    
    }

    return !hasForbiddenCharacters(input);
  
  });

  configure({
    validateOnBlur: true,
    validateOnChange: true,
    validateOnInput: true,
    validateOnModelUpdate: true,
    generateMessage: (ctx: ValidationMessageContext) => {

      const ruleName = ctx.rule?.name || 'required';
      const fieldLabel = resolveFieldLabel(ctx, composer);
      const rawParams = ctx.rule?.params;
      const primaryParam = Array.isArray(rawParams) ? rawParams[0] : undefined;
      const forbiddenCharsInValue = extractForbiddenCharacters(String(ctx.value ?? ''));
      const charactersParam = forbiddenCharsInValue.length
        ? forbiddenCharsInValue.join(', ')
        : FORBIDDEN_CHARACTERS_MESSAGE;

      const messageParams = {
        field: fieldLabel,
        _field_: fieldLabel,
        length: primaryParam,
        amount_of_digits: primaryParam,
        characters: charactersParam,
      };

      const translated = translateRuleMessage({
        composer,
        ruleName,
        params: messageParams,
      });

      if (translated) {

        return translated;
      
      }

      if (ruleName === 'noForbiddenCharacters') {

        return composer?.t?.('validation.noForbiddenCharacters', messageParams)
          ?? `Forbidden characters are not allowed (${charactersParam})`;

      }

      if (ruleName === 'digits') {

        return composer?.t?.('validation.digits', messageParams)
          ?? FALLBACK_MESSAGES.digits(fieldLabel, primaryParam);

      }

      const fallback = FALLBACK_MESSAGES[ruleName] ?? FALLBACK_MESSAGES.default;

      return fallback(fieldLabel, primaryParam);

    },
  });

});
